/**
 * KeyValueList - Label: value pairs display
 *
 * Usage:
 *   <KeyValueList
 *     title="Contact Details"
 *     data={{
 *       items: [
 *         { label: 'Email', value: 'john@example.com', type: 'email', icon: '📧' },
 *         { label: 'Phone', value: '+1 555-1234', type: 'phone', icon: '📱' },
 *         { label: 'Location', value: 'San Francisco, CA', icon: '📍' },
 *       ],
 *       layout: 'vertical'
 *     }}
 *   />
 */

import type { BlockProps, KeyValueListData, KeyValueItem } from './types';
import './blocks.css';

export interface KeyValueListProps extends BlockProps<KeyValueListData> {
  /** Compact mode with smaller text */
  compact?: boolean;
}

export function KeyValueList({
  data,
  title,
  compact = false,
  className,
}: KeyValueListProps) {
  const { items, layout = 'vertical' } = data;

  if (!items || items.length === 0) {
    return (
      <div className={`key-value-list ${className || ''}`}>
        {title && <div className="key-value-list-title">{title}</div>}
        <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--color-text-faint)' }}>
          No data available
        </div>
      </div>
    );
  }

  const getLayoutStyles = (): React.CSSProperties => {
    switch (layout) {
      case 'horizontal':
        return {
          display: 'flex',
          flexWrap: 'wrap',
          gap: compact ? '12px 24px' : '16px 32px',
        };
      case 'grid':
        return {
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: compact ? '12px' : '16px',
        };
      default: // vertical
        return {
          display: 'flex',
          flexDirection: 'column',
          gap: compact ? '8px' : '12px',
        };
    }
  };

  return (
    <div className={`key-value-list ${className || ''}`}>
      {title && (
        <div style={{
          fontSize: 'var(--text-sm)',
          fontWeight: 600,
          color: 'var(--color-text)',
          marginBottom: compact ? '12px' : '16px',
        }}>
          {title}
        </div>
      )}

      <div style={getLayoutStyles()}>
        {items.map((item, index) => (
          <KeyValueRow key={index} item={item} compact={compact} layout={layout} />
        ))}
      </div>
    </div>
  );
}

function KeyValueRow({
  item,
  compact,
  layout,
}: {
  item: KeyValueItem;
  compact: boolean;
  layout: string;
}) {
  const { label, value, icon, type, href } = item;

  const renderValue = () => {
    const formattedValue = formatValue(value, type);

    if (type === 'link' && href) {
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: 'var(--color-primary)',
            textDecoration: 'none',
          }}
        >
          {formattedValue}
        </a>
      );
    }

    if (type === 'email') {
      return (
        <a
          href={`mailto:${value}`}
          style={{
            color: 'var(--color-primary)',
            textDecoration: 'none',
          }}
        >
          {formattedValue}
        </a>
      );
    }

    if (type === 'phone') {
      return (
        <a
          href={`tel:${value}`}
          style={{
            color: 'var(--color-primary)',
            textDecoration: 'none',
          }}
        >
          {formattedValue}
        </a>
      );
    }

    return formattedValue;
  };

  if (layout === 'horizontal') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {icon && <span style={{ fontSize: compact ? '14px' : '16px' }}>{icon}</span>}
        <span style={{
          fontSize: compact ? '12px' : '13px',
          color: 'var(--color-text-muted)',
        }}>
          {label}:
        </span>
        <span style={{
          fontSize: compact ? '13px' : '14px',
          color: 'var(--color-text)',
          fontWeight: 500,
        }}>
          {renderValue()}
        </span>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      padding: compact ? '8px 0' : '10px 0',
      borderBottom: '1px solid var(--color-border)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {icon && <span style={{ fontSize: compact ? '14px' : '16px' }}>{icon}</span>}
        <span style={{
          fontSize: compact ? '12px' : '13px',
          color: 'var(--color-text-muted)',
        }}>
          {label}
        </span>
      </div>
      <span style={{
        fontSize: compact ? '13px' : '14px',
        color: 'var(--color-text)',
        fontWeight: 500,
        textAlign: 'right',
        maxWidth: '60%',
        wordBreak: 'break-word',
      }}>
        {renderValue()}
      </span>
    </div>
  );
}

function formatValue(value: string | number, type?: string): string {
  if (value === undefined || value === null) return '—';

  switch (type) {
    case 'number':
      return typeof value === 'number' ? value.toLocaleString() : String(value);
    case 'date':
      return new Date(String(value)).toLocaleDateString();
    default:
      return String(value);
  }
}

export default KeyValueList;
